import React, { useState, useEffect } from 'react';
import styles from './ReleaseScheduleSection.module.css';
import Ico from '../common/Ico';
import Pill from '../common/Pill';
import Btn from '../common/Btn';
import { setAlert } from '../../helpers/alerts';
import { apiUpdateBranch } from '../../api/branches';
import { formatDateTimeUTC, timeUntilTS } from '../../helpers/dates';

const ICO_CLOCK = 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 7v5l3 2';
const ICO_INFO  = 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 16v-4M12 8h.01';

export default function ReleaseScheduleSection({ branch, nextVersion, reloadData }) {
  const [days, setDays]       = useState(branch.release_frequency_days ?? 14);
  const [saving, setSaving]   = useState(false);

  // Re-sync when the branch reloads (websocket update, or another tab saved).
  useEffect(() => { setDays(branch.release_frequency_days ?? 14); }, [branch.release_frequency_days]);

  const enabled = !!branch.auto_release_enabled;
  const dirty   = Number(days) !== branch.release_frequency_days;
  const valid   = Number.isInteger(Number(days)) && Number(days) >= 1 && Number(days) <= 365;

  const save = (patch, message) => {
    setSaving(true);
    apiUpdateBranch(branch.version, patch)
      .then(() => { setAlert('Release schedule', message, 'success'); reloadData?.(); })
      .catch((e) => setAlert('Release schedule error', e, 'danger'))
      .finally(() => setSaving(false));
  };

  const handleToggle = () => save(
    { auto_release_enabled: !enabled, release_frequency_days: Number(days) },
    `Automatic releases ${enabled ? 'disabled' : 'enabled'} for "${branch.version}"`,
  );

  const handleSaveCadence = () => save(
    { release_frequency_days: Number(days) },
    `Release frequency set to ${days} days for "${branch.version}"`,
  );

  // nextVersion is null when the scheduler would ignore this branch entirely.
  const ignored = !nextVersion;

  return (
    <section className={styles.section} id="sec-release-schedule">
      <div className={styles.header}>
        <span className={styles.iconTile}>
          <Ico d={ICO_CLOCK} size={16} strokeWidth={1.7} />
        </span>
        <div className={styles.headerText}>
          <div className={styles.titleRow}>
            <span className={styles.title}>Automatic releases</span>
            <Pill kind={enabled ? 'ok' : 'neutral'} dot>{enabled ? 'On' : 'Off'}</Pill>
          </div>
          <div className={styles.subtitle}>
            Cuts the next patch release on a fixed cadence, announced in Slack an hour ahead.
          </div>
        </div>
        <Btn
          variant={enabled ? 'warn' : 'primary'}
          size="lg"
          loading={saving}
          onClick={handleToggle}
        >
          {enabled ? 'Disable' : 'Enable'}
        </Btn>
      </div>

      <div className={styles.body}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="release-frequency">Release every</label>
          <div className={styles.inputRow}>
            <input
              id="release-frequency"
              type="number"
              min={1}
              max={365}
              className={`${styles.input}${valid ? '' : ` ${styles.inputError}`}`}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              aria-invalid={!valid}
              aria-describedby={valid ? undefined : 'release-frequency-error'}
            />
            <span className={styles.unit}>days</span>
            {dirty && (
              <Btn variant="soft" size="md" loading={saving} disabled={!valid} onClick={handleSaveCadence}>
                Save
              </Btn>
            )}
          </div>
          {!valid && (
            <span id="release-frequency-error" className={styles.error}>
              Must be between 1 and 365 days
            </span>
          )}
        </div>

        <div className={styles.divider} />

        <div className={styles.field}>
          <span className={styles.label}>Next release</span>
          {ignored ? (
            <div className={styles.notice}>
              <Ico d={ICO_INFO} size={14} className={styles.noticeIcon} />
              <span>
                This branch is skipped — its last release is not a plain <code>x.y.z</code> version
                to increment from.
              </span>
            </div>
          ) : enabled ? (
            <div className={styles.nextRow}>
              <span className={styles.nextVersion}>{nextVersion}</span>
              <span className={styles.nextWhen}>
                {formatDateTimeUTC(branch.next_release_at)}
                <span className={styles.relative}>{timeUntilTS(branch.next_release_at)}</span>
              </span>
            </div>
          ) : (
            <div className={styles.nextRow}>
              <span className={styles.nextVersion}>{nextVersion}</span>
              <span className={styles.nextWhen}>when enabled</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
